import { motion } from 'motion/react'
import type { ReactNode } from 'react'

import { AstraParagraph } from '../../../Typography/AstraParagraph/AstraParagraph'
import { astraTwMerge } from '../../../Utilities/astraTwMerge'
import type { AstraFilterRange, AstraFilterValue } from '../AstraFilterValueButton'
import { segmentStyles, textColor } from './filterValueButtonStyles'

// The values are the part that changes, so they carry the affordance; they stay lit while the menu is open.
const hoverStyles = 'group-hover:bg-neutral-subtle-hover group-aria-expanded:bg-neutral-subtle-hover'

// Long values truncate past this, so one filter never takes over the row.
const valuesStyles = astraTwMerge(segmentStyles, 'max-w-[250px]')

// Past a few, more overlapping icons stop telling the values apart and only widen the button.
const MAX_STACKED_ICONS = 3

/** Icons overlapping left to right, each on top of the one before it. */
const StackedIcons = ({ icons }: { icons: ReactNode[] }) => (
    <span aria-hidden className="flex shrink-0 items-center">
        {icons.map((icon, index) => (
            <span key={index} className={astraTwMerge('relative flex shrink-0 [&>svg]:relative [&>svg]:size-3.5', index < icons.length - 1 && '-mr-1.5')}>
                {/* Keeps the icon underneath from showing through this one's cut-outs. */}
                <span className="absolute inset-0.5 bg-white" />
                {icon}
            </span>
        ))}
    </span>
)

/**
 * A range as `from – to`, or by its one closed end. The dash is read out as "til", which is what it means.
 */
const RangeLabel = ({ range: { from, to } }: { range: AstraFilterRange }) => {
    if (from !== undefined && to !== undefined)
        return (
            <>
                {from}
                <span aria-hidden> – </span>
                <span className="sr-only"> til </span>
                {to}
            </>
        )
    return from !== undefined ? `Fra ${from}` : `Til ${to}`
}

type ValuesProps = {
    values: AstraFilterValue[]
    range?: AstraFilterRange
    /** If `true`, several values are shown as their icons stacked and a count instead of by name. */
    collapsed: boolean
    disabled?: boolean
    muted?: boolean
}

/**
 * The values by name, each after its icon, or, when collapsed, as their icons stacked and a count such as
 * "2 valgte", leaving the names to the menu. A single value is always shown by name.
 */
const Values = ({ values, range, collapsed, disabled, muted }: ValuesProps) => {
    const color = textColor(disabled, muted)

    if (range) {
        return (
            <AstraParagraph as="span" variant="body-small" weight="medium" className={astraTwMerge('truncate', color)}>
                <RangeLabel range={range} />
            </AstraParagraph>
        )
    }

    if (!collapsed || values.length < 2) {
        return values.map((value, index) => (
            <span key={index} className="flex min-w-0 items-center gap-1">
                {value.icon && (
                    <span aria-hidden className="flex shrink-0 [&>svg]:size-3.5">
                        {value.icon}
                    </span>
                )}
                <AstraParagraph as="span" variant="body-small" weight="medium" className={astraTwMerge('truncate', color)}>
                    {value.label}
                    {/* The next value's icon already separates it from this one. */}
                    {index < values.length - 1 && !values[index + 1]?.icon && ','}
                </AstraParagraph>
            </span>
        ))
    }

    const icons = values.flatMap(value => (value.icon ? [value.icon] : [])).slice(0, MAX_STACKED_ICONS)
    return (
        <>
            {icons.length > 0 && <StackedIcons icons={icons} />}
            <AstraParagraph as="span" variant="body-small" weight="medium" className={astraTwMerge('truncate', color)}>
                {values.length} valgte
                {/* The names only fit in the menu, so a screen reader hears them here instead. */}
                <span className="sr-only"> ({values.map(value => value.label).join(', ')})</span>
            </AstraParagraph>
        </>
    )
}

type FilterValuesProps = ValuesProps & {
    /** If `true`, the values open a menu, so they light up on hover. */
    editable: boolean
}

/**
 * The segment showing what the filter is set to. It resizes with the button's layout animation, and its
 * content is counter-scaled so the text is never stretched.
 *
 * Both views are also rendered hidden at their natural width, for AstraFilterValueButtonGroup to decide what
 * fits.
 */
export const FilterValues = ({ editable, ...props }: FilterValuesProps) => {
    const { values, range, disabled } = props

    return (
        <motion.span
            layout
            data-filter-value-part="values"
            className={astraTwMerge(valuesStyles, 'relative overflow-hidden', editable && !disabled && hoverStyles)}
        >
            <motion.span layout="position" className="flex min-w-0 items-center gap-1">
                <Values {...props} />
            </motion.span>
            <span aria-hidden className="pointer-events-none invisible absolute left-0 top-0 flex w-max">
                <span data-filter-value-part="expanded" className={valuesStyles}>
                    <Values values={values} range={range} collapsed={false} />
                </span>
                {!range && values.length > 1 && (
                    <span data-filter-value-part="collapsed" className={valuesStyles}>
                        <Values values={values} collapsed />
                    </span>
                )}
            </span>
        </motion.span>
    )
}
